import { AbstractControl, ValidationErrors } from '@angular/forms';

/**
 * The one stored/compared form of an IBAN: all whitespace removed, letters in capitals
 * (`NL19rabo 0117 1046 12` -> `NL19RABO0117104612`). Anything that is not a string reads as
 * "no value" (''), so callers can pass a raw control value without guarding it first.
 *
 * Grouping in blocks of four is a display concern and lives with the component that wants it
 * (iban-bic.component.ts); the plain input stores the compact form.
 */
export function normalizeIban(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\s/g, '').toUpperCase() : '';
}

/**
 * IBAN format + mod-97 checksum (ISO 13616). Spaces and letter case are ignored. Shared by the
 * `iban-bic` field and the "Validatie: IBAN" option on a plain input (input-validation.extension.ts).
 */
export function ibanValidator(control: AbstractControl): ValidationErrors | null {
  // A non-string value (a number that ended up in the control) is stringified rather than read as
  // empty: it must be rejected, not waved through as "no value".
  const value = control.value == null ? '' : normalizeIban(String(control.value));

  if (!value) {
    // no value = valid; use required for mandatory fields
    return null;
  }

  if (!/^[A-Z]{2}[0-9A-Z]{13,32}$/.test(value)) {
    return { invalidIban: true };
  }

  // Move the first four characters to the end, then letters to numbers (A=10, B=11, …)
  const rearranged = value.substring(4) + value.substring(0, 4);
  const numerical = rearranged
    .split('')
    .map(char => (/[0-9]/.test(char) ? char : (char.charCodeAt(0) - 55).toString()))
    .join('');

  let remainder = 0;
  for (let i = 0; i < numerical.length; i++) {
    remainder = (remainder * 10 + parseInt(numerical[i], 10)) % 97;
  }

  return remainder === 1 ? null : { invalidIban: true };
}
